import React, { useCallback, useEffect, useRef, useState } from "react";

import { DROPDOWN_PAGE_SIZE, FILTER_DEBOUNCE_MS } from "../../../config/config";
import { ROUTE_CALENDAR } from "../../../config/lang";
import { call } from "../../../util/action";
import { translate } from "../../../util/util";

// The MIME type used to carry a dragged point between the panel and calendar cells.
export const POINT_DND_TYPE = "application/x-picsell-point";

// Left pane: a debounced, server-searched, draggable list of points. The list is PAGINATED —
// it loads the next page as you scroll (points/?page=N), so every point is reachable, not just
// the first page. Dragging a row onto a calendar day adds the stop (native HTML5 DnD).
export default function PointSearchPanel({ lang }) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const pageRef = useRef(1);
  const loadingRef = useRef(false); // synchronous guard so fast scroll can't double-fetch
  const debounceRef = useRef(null);

  const fetchPage = useCallback((pageNum, search, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    const q = search ? `&search=${encodeURIComponent(search)}` : "";
    call({ method: "GET", url: `points/?page=${pageNum}&page_size=${DROPDOWN_PAGE_SIZE}${q}` }, resp => {
      const items = Array.isArray(resp) ? resp : resp?.results || [];
      const more = Array.isArray(resp) ? false : Boolean(resp?.next);
      pageRef.current = pageNum;
      setResults(prev => (append ? [...prev, ...items] : items));
      setHasMore(more);
      loadingRef.current = false;
      setLoading(false);
    });
  }, []);

  // Debounced search resets to the first page.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPage(1, term, false), FILTER_DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [term, fetchPage]);

  const onScroll = e => {
    const el = e.currentTarget;
    if (hasMore && !loadingRef.current && el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
      fetchPage(pageRef.current + 1, term, true);
    }
  };

  const onDragStart = (e, point) => {
    // Carry only the fields the calendar/save path needs.
    const payload = { id: point.id, name: point.name, address: point.address, locality: point.locality };
    e.dataTransfer.setData(POINT_DND_TYPE, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <div className="rc-points-panel">
      <div className="rc-points-search">
        <span className="material-symbols-outlined">search</span>
        <input
          type="text"
          value={term}
          onChange={e => setTerm(e.target.value)}
          placeholder={translate(ROUTE_CALENDAR, "search_placeholder", lang)}
        />
      </div>
      <div className="rc-points-list" onScroll={onScroll}>
        {!loading && results.length === 0 && (
          <div className="rc-points-hint">{translate(ROUTE_CALENDAR, "no_points_found", lang)}</div>
        )}
        {results.map(point => (
          <div
            key={point.id}
            className="rc-point-chip"
            draggable
            onDragStart={e => onDragStart(e, point)}
            title={point.address || ""}
          >
            <span className="material-symbols-outlined rc-drag-handle">drag_indicator</span>
            <span className="rc-point-chip-body">
              <span className="rc-point-chip-name">{point.name}</span>
              {(point.locality?.name || point.address) && (
                <span className="rc-point-chip-sub">{point.locality?.name || point.address}</span>
              )}
            </span>
          </div>
        ))}
        {loading && <div className="rc-points-hint">…</div>}
      </div>
    </div>
  );
}
